'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { JobSitePin, PinTone } from './OperationsHeatMap';
import DemoDataPill from './DemoDataPill';

const OperationsHeatMap = dynamic(() => import('./OperationsHeatMap'), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-80 rounded-xl" style={{ background: '#f1f5f9' }}>
      <div className="flex flex-col items-center gap-2">
        <div className="w-8 h-8 rounded-full border-[3px] border-[#07B0E9] border-t-transparent animate-spin" />
        <span className="text-xs text-gray-500">Loading map…</span>
      </div>
    </div>
  ),
});

// ── Mock job pins — NextRoof coverage cities ───────────────────────────────
// Coords approximate to city center; replace with real lat/lng from
// roofing_jobs when we wire real data.
const MOCK_PINS: Array<Omit<JobSitePin, 'tone'>> = [];

// ── Open-Meteo weather code → severity tone ────────────────────────────────
// https://open-meteo.com/en/docs (WMO weather codes).
// severe = thunderstorm (95–99), hail (96/99), OR wind > 25 mph
// rain   = drizzle / rain / showers / snow (51–86)
// clear  = clear / partly cloudy / fog (0–48)
function codeToTone(code: number, windMph: number): PinTone {
  if (windMph > 25) return 'severe';
  if (code >= 95) return 'severe';
  if (code >= 51 && code <= 86) return 'rain';
  if (code >= 0 && code <= 48) return 'clear';
  return 'unknown';
}

// One Open-Meteo fetch for a single pin. Races against an 8s timeout.
async function fetchPinWeather(
  pin: { id: string; lat: number; lng: number },
  signal: AbortSignal,
): Promise<{ id: string; tone: PinTone }> {
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${pin.lat}&longitude=${pin.lng}` +
    `&current=weather_code,wind_speed_10m` +
    `&wind_speed_unit=mph&timezone=auto`;

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(`pin ${pin.id} weather timed out`)), 8000),
  );

  const res = await Promise.race([fetch(url, { signal }), timeoutPromise]);
  if (!res.ok) throw new Error(`pin ${pin.id} HTTP ${res.status}`);
  const data = await res.json();
  const code = Number(data?.current?.weather_code ?? -1);
  const wind = Number(data?.current?.wind_speed_10m ?? 0);
  return { id: pin.id, tone: codeToTone(code, wind) };
}

export default function Row3Map() {
  const [pins, setPins] = useState<JobSitePin[]>(
    MOCK_PINS.map((p) => ({ ...p, tone: 'unknown' as PinTone })),
  );
  const abortRef = useRef<AbortController | null>(null);

  // Stagger pin weather fetches by 150ms each, then settle in parallel.
  // Promise.allSettled — one bad pin shouldn't gray out the rest.
  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    const run = async () => {
      const inflight: Promise<{ id: string; tone: PinTone }>[] = [];
      for (let i = 0; i < MOCK_PINS.length; i++) {
        if (cancelled || controller.signal.aborted) return;
        if (i > 0) await new Promise((r) => setTimeout(r, 150));
        if (cancelled || controller.signal.aborted) return;
        inflight.push(fetchPinWeather(MOCK_PINS[i], controller.signal));
      }

      const settled = await Promise.allSettled(inflight);
      if (cancelled) return;

      const toneById = new Map<string, PinTone>();
      settled.forEach((result, idx) => {
        const pinId = MOCK_PINS[idx].id;
        if (result.status === 'fulfilled') {
          toneById.set(pinId, result.value.tone);
        } else {
          console.warn('[OpsMap] weather fetch failed for', pinId, result.reason);
          toneById.set(pinId, 'unknown');
        }
      });

      setPins(
        MOCK_PINS.map((p) => ({
          ...p,
          tone: toneById.get(p.id) ?? 'unknown',
        })),
      );
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return (
    <section
      className="relative rounded-xl overflow-hidden"
      style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
    >
      <DemoDataPill />
      <div className="px-5 pt-5 pb-3">
        <h3
          className="text-base font-bold"
          style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
        >
          Active Job Sites
        </h3>
        <p className="text-xs text-gray-400 mt-0.5">
          {pins.length} active jobs · weather overlay refreshes per pin via Open-Meteo
        </p>
      </div>
      <div style={{ height: '420px', position: 'relative' }}>
        <OperationsHeatMap pins={pins} />
        {/* Legend overlay */}
        <div
          className="absolute top-3 right-3 z-[1000] rounded-lg px-3 py-2 text-xs"
          style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', border: '1px solid #E5E7EB' }}
        >
          <p className="font-bold uppercase tracking-wider text-[10px] text-gray-500 mb-1.5">Weather overlay</p>
          <div className="space-y-1">
            <LegendDot color="#10B981" label="Clear" />
            <LegendDot color="#F59E0B" label="Rain expected" />
            <LegendDot color="#EF4444" label="Severe" />
            <LegendDot color="#9CA3AF" label="No data" />
          </div>
        </div>
      </div>
    </section>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <span className="text-gray-700">{label}</span>
    </div>
  );
}
